<template>
  <!--
    Formulario "Ver certificación actual" (pictogramas).
    Carga subcontratistas con PartnerProcess de la entidad (mecánica / eléctrica / civil)
    y, al elegir uno, sus órdenes de pedido del proyecto.
  -->
  <div>
    <b-field :label="$t('subcontractor')" class="mb-3">
      <multiselect
        v-model="selectedPartner"
        :options="partners"
        label="name"
        track-by="id"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :placeholder="$t('select')"
        :loading="isLoading"
        :disabled="isLoading"
        @input="onPartnerChanged"
      >
        <template slot="noResult">{{ $t('no_elements_found') }}</template>
        <template slot="noOptions">{{ $t('no_options_available') }}</template>
      </multiselect>
    </b-field>

    <b-field :label="$t('work_order')" class="mb-3">
      <multiselect
        v-model="selectedOrder"
        :options="currentOrders"
        label="code"
        track-by="id"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :placeholder="$t('select_work_order')"
        :loading="isLoading"
        :disabled="!selectedPartner || isLoading"
      >
        <template slot="noResult">{{ $t('no_elements_found') }}</template>
        <template slot="noOptions">{{ $t('no_options_available') }}</template>
      </multiselect>
    </b-field>

    <p v-if="!isLoading && partners.length === 0" class="text-xs text-gray-500 mb-3">
      {{ $t('no_results') }}
    </p>

    <!-- Footer -->
    <div class="flex justify-end gap-2 mt-4">
      <b-button size="is-small" @click="$emit('close')">{{ $t('cancel') }}</b-button>
      <b-button
        type="is-primary"
        size="is-small"
        icon-left="eye"
        icon-pack="fas"
        :loading="isViewing"
        :disabled="isLoading"
        @click="viewCurrent"
      >
        {{ $t('view_current_certification') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Multiselect from 'vue-multiselect'

export default {
  name: 'ViewCurrentCertificationForm',

  components: {
    Multiselect,
  },

  props: {
    projectId: { type: Number, default: null },
    // PartnerProcessEntity: 1=obra civil, 2=montaje mecánico, 3=instalación eléctrica.
    entityType: { type: Number, default: null },
    // Prefill opcional (p.ej. desde el icono de una fila del listado).
    initialPartnerId: { type: Number, default: null },
    initialOrderId: { type: Number, default: null },
  },

  data: () => ({
    partners: [],
    selectedPartner: null,
    selectedOrder: null,
    currentOrders: [],
    isLoading: false,
    isViewing: false,
  }),

  computed: {
    // civil | mechanical | electrical — requerido por el endpoint de preview.
    pictogramType() {
      if (Number(this.entityType) === 1) return 'civil'
      return Number(this.entityType) === 3 ? 'electrical' : 'mechanical'
    },
  },

  created() {
    this.loadPartners()
  },

  methods: {
    async loadPartners() {
      if (!this.projectId || !this.entityType) {
        return
      }
      this.isLoading = true
      this.partners = []
      this.currentOrders = []
      this.selectedPartner = null
      this.selectedOrder = null
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/certification-partners`,
          { params: { entity_type: this.entityType } }
        )
        this.partners = (data && data.success && data.data) ? data.data : []
        this.applyInitialSelection()
      } catch (e) {
        this.partners = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoading = false
      }
    },

    // Si vienen props de prefill (fila del listado), seleccionamos partner + orden.
    applyInitialSelection() {
      if (!this.initialPartnerId || !this.partners.length) {
        return
      }
      const partner = this.partners.find(p => Number(p.id) === Number(this.initialPartnerId))
      if (!partner) {
        return
      }
      this.selectedPartner = partner
      this.currentOrders = partner.orders || []
      if (this.initialOrderId) {
        this.selectedOrder = this.currentOrders.find(o => Number(o.id) === Number(this.initialOrderId)) || null
      }
    },

    // Al cambiar de subcontratista, refrescamos sus ordenes.
    onPartnerChanged(partner) {
      this.currentOrders = partner ? (partner.orders || []) : []
      this.selectedOrder = null
    },

    async viewCurrent() {
      if (!this.selectedPartner) {
        this.$notify.warning('select_subcontractor')
        return
      }
      if (!this.selectedOrder) {
        this.$notify.warning('select_work_order')
        return
      }
      if (!this.projectId) {
        this.$notify.error('error_loading_data')
        return
      }

      this.isViewing = true
      try {
        const payload = {
          partner_id: this.selectedPartner.id,
          order_id: this.selectedOrder.id,
          type: this.pictogramType,
        }
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/pictograms/certifications/view-current`,
          payload
        )

        if (!data || !data.success) {
          this.$notify.error((data && data.error) || 'error')
          return
        }

        const url = data.documentUrl || data.docUrl || data.url
        if (!url) {
          this.$notify.error('error_loading_data')
          return
        }

        window.open(url, '_blank')
        this.$emit('close')
      } catch (e) {
        this.$notify.error('error_loading_data')
      } finally {
        this.isViewing = false
      }
    },
  },
}
</script>
